import React, { useState } from "react";
import { Alert, Row, Col, Form, Button, UncontrolledTooltip } from "reactstrap";

// hooks
import { useRedux } from "../../hooks/index";
import { useNavigate } from "react-router-dom";

// router
import { Link, Navigate } from "react-router-dom";

// validations
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useForm } from "react-hook-form";

// hooks
import { useProfile } from "../../hooks";
import { createSelector } from "reselect";
// actions
import { registerUser } from "../../redux/actions";

// components
import NonAuthLayoutWrapper from "../../components/NonAutnLayoutWrapper";
import AuthHeader from "../../components/AuthHeader";
import FormInput from "../../components/FormInput";
import Loader from "../../components/Loader";
import { registerUserAccount } from "../../services/auth";
import { redirectAfterDelay } from "../../utils/redirectAfterDelay";

import { useSnackbar } from "../../utils/snackbar";
import { useTranslation } from "react-i18next";

interface RegisterProps {}
const Register = (props: RegisterProps) => {
  // global store
  const { dispatch, useAppSelector } = useRedux();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const handleLogoClick = () => {
    navigate("/landing-page");
  };
  interface RegisterFormValues {
    full_name: string;
    email: string;
    password: string;
  }

  const errorData = createSelector(
    (state: any) => state.Register,
    state => ({
      user: state.user,
      registrationError: state.registrationError,
      regLoading: state.loading,
      isUserRegistered: state.isUserRegistered,
    }),
  );

  const showSnackbar = useSnackbar();
  const { user, registrationError, regLoading } = useAppSelector(errorData);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resolver = yupResolver(
    yup.object().shape({
      full_name: yup.string().required("Please enter your full name."),
      email: yup
        .string()
        .email("This value should be a valid email.")
        .required("Please enter an email."),
      password: yup.string().required("Please enter a password."),
    }),
  );

  const defaultValues: any = {};

  const methods = useForm({ defaultValues, resolver });
  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
  } = methods;

  const onSubmitForm = async (values: RegisterFormValues) => {
    setIsSubmitting(true);
    try {
      const addtionalData = {
        full_name: values.full_name,
      };
      const response = await registerUserAccount(
        values.email,
        values.password,
        addtionalData,
      );

      if (response.success) {
        showSnackbar("Verification email sent!", "info");
      } else {
        if (
          response.message == "Firebase: Error (auth/email-already-in-use)."
        ) {
          showSnackbar("User was already registered.", "error");
        } else {
          showSnackbar(
            response.message || "Registration failed. Please try again.",
            "error",
          );
        }
        setAlertType("danger");
      }
    } catch (error) {
      setAlertMessage(
        "An error occurred while registering. Please try again later.",
      );
      setAlertType("danger");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRedirect = () => {
    redirectAfterDelay(navigate, "/auth-login", 1000); // Redirects to "/target-page" after 3 seconds
  };

  const { userProfile, loading } = useProfile();

  if (userProfile && !loading) {
    return <Navigate to={{ pathname: "/dashboard" }} />;
  }

  return (
    <NonAuthLayoutWrapper>
      <Row className=" justify-content-center my-auto">
        <Col sm={8} lg={6} xl={5} className="col-xxl-4">
          <div className="py-md-5 py-4">
            <AuthHeader
              title={t("Register Account")}
              subtitle={t("Get your free Invoice account now.")}
            />

            {alertMessage && (
              <Alert color={alertType} toggle={() => setAlertMessage("")}>
                {alertMessage}
              </Alert>
            )}

            <Form
              onSubmit={handleSubmit(onSubmitForm)}
              className="position-relative"
            >
              {regLoading && <Loader />}

              <div className="mb-3">
                <FormInput
                  label={t("Full Name")}
                  type="text"
                  name="full_name"
                  register={register}
                  errors={errors}
                  control={control}
                  labelClassName="form-label"
                  placeholder={t("Enter Full Name")}
                  className="form-control"
                />
              </div>

              <div className="mb-3">
                <FormInput
                  label={t("Email")}
                  type="text"
                  name="email"
                  register={register}
                  errors={errors}
                  control={control}
                  labelClassName="form-label"
                  placeholder={t("Enter Email")}
                  className="form-control"
                />
              </div>

              <div className="mb-3">
                <FormInput
                  label={t("Password")}
                  type="password"
                  name="password"
                  register={register}
                  errors={errors}
                  control={control}
                  labelClassName="form-label"
                  className="form-control pe-5"
                  placeholder={t("Enter Password")}
                />
              </div>

              <div className="mb-4">
                <p className="mb-0">
                  {t("By registering you agree to the Invoice")}{" "}
                  <Link to="/terms" className="text-primary">
                    {t("Terms of Use")}
                  </Link>
                </p>
              </div>

              <div className="text-center mb-3">
                <Button
                  color="primary"
                  className="w-100 waves-effect waves-light"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t("Processing...") : t("Register")}
                </Button>
              </div>
            </Form>

            <div className="mt-5 text-center text-muted">
              <p>
                {t("Already have an account?")}{" "}
                <Link
                  to="/auth-login"
                  className="fw-medium text-decoration-underline"
                >
                  {t("Login")}
                </Link>
              </p>
            </div>
          </div>
        </Col>
      </Row>
    </NonAuthLayoutWrapper>
  );
};

export default Register;
